import React from 'react';
import {Button, Card, Form, Table} from 'react-bootstrap';
import {useSelector} from 'react-redux';
import {getAdminShopOrders, getPartnerShopOrders} from '../api';
import {useTranslation} from 'react-i18next';

const euros = value => value == null ? '0.00' : (Number(value) / 100).toFixed(2);

const statusKeys = {paid: 'SHOP_ORDER_PAID', preparing: 'SHOP_ORDER_PREPARING', shipped: 'SHOP_ORDER_SHIPPED', delivered: 'SHOP_ORDER_DELIVERED', cancelled: 'SHOP_ORDER_CANCELLED'};

const ShopReport = ({scope = 'admin'}) => {
  const {t, i18n} = useTranslation();
  const statusLabel = value => statusKeys[value] ? t(statusKeys[value]) : value;
  const token = useSelector(state => state.login && state.login.token);
  const [orders, setOrders] = React.useState([]);
  const [status, setStatus] = React.useState('');
  const [partnerFilter, setPartnerFilter] = React.useState('all');
  const [partnerId, setPartnerId] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const isPartner = scope === 'partner';

  const load = React.useCallback(async () => {
    if (!token) return;
    if (!isPartner && partnerFilter === 'partner' && !String(partnerId).trim()) {
      setOrders([]);
      setMessage(t('SHOP_REPORT_PARTNER_ID_REQUIRED'));
      return;
    }
    setLoading(true);
    const options = isPartner
      ? {}
      : partnerFilter === 'all'
        ? {allPartners: true}
        : partnerFilter === 'replayer'
          ? {}
          : {partnerId};
    const response = isPartner
      ? await getPartnerShopOrders(token, status)
      : await getAdminShopOrders(token, status, options);
    if (response.status === 200) {
      setOrders(Array.isArray(response.json) ? response.json : []);
      setMessage('');
    } else {
      setMessage(response.json.error || t('SHOP_REPORT_LOAD_ERROR'));
    }
    setLoading(false);
  }, [token, status, partnerFilter, partnerId, isPartner, t]);

  React.useEffect(() => { load(); }, [load]);

  const total = orders.reduce((sum, order) => sum + Number(order.total_cents || 0), 0);
  const shippingTotal = orders.reduce((sum, order) => sum + Number(order.shipping_cents || 0), 0);

  return (
    <Card className="partner-report-skywork-shop">
      <Card.Body>
        <div className="partner-report-skywork-shop-head">
          <div>
            <span className="partner-report-skywork-card-icon"><i className="fa fa-shopping-bag"/></span>
            <div><h4>{t('SHOP_REPORT')}</h4><p>{t('SHOP_REPORT_SUMMARY', {count: orders.length, total: euros(total), shipping: euros(shippingTotal)})}</p></div>
          </div>
          <div className="partner-report-skywork-shop-filters">
            {!isPartner && (
              <Form.Control
                as="select"
                size="sm"
                className="mr-2 mb-2"
                value={partnerFilter}
                onChange={event => setPartnerFilter(event.target.value)}
              >
                <option value="all">{t('ALL')}</option>
                <option value="replayer">{t('REPLAYER_ONLY')}</option>
                <option value="partner">{t('PARTNER_ID_FILTER')}</option>
              </Form.Control>
            )}
            {!isPartner && partnerFilter === 'partner' && (
              <Form.Control
                size="sm"
                className="mr-2 mb-2"
                placeholder={t('PARTNER_ID')}
                value={partnerId}
                onChange={event => setPartnerId(event.target.value)}
              />
            )}
            <Form.Control
              as="select"
              size="sm"
              className="mr-2 mb-2"
              value={status}
              onChange={event => setStatus(event.target.value)}
            >
              <option value="">{t('ALL_STATUSES')}</option>
              {Object.keys(statusKeys).map(value => (
                <option key={value} value={value}>{statusLabel(value)}</option>
              ))}
            </Form.Control>
            <Button size="sm" className="partner-report-skywork-secondary" onClick={load} disabled={loading}>
              <i className={`fa ${loading ? 'fa-circle-o-notch fa-spin' : 'fa-refresh'}`}/> {loading ? t('LOADING') : t('UPDATE')}
            </Button>
          </div>
        </div>
        {message && <div className="alert alert-warning">{message}</div>}
        <div className="partner-report-skywork-table-wrap"><Table responsive hover className="partner-report-skywork-table">
          <thead>
            <tr>
              <th>{t('ORDER')}</th>
              <th>{t('SHOP')}</th>
              <th>{t('CUSTOMER')}</th>
              <th>{t('DATE')}</th>
              <th>{t('DELIVERY')}</th>
              <th>{t('ITEMS')}</th>
              <th>{t('SHIPPING')}</th>
              <th>{t('TOTAL')}</th>
              <th>{t('STATE')}</th>
            </tr>
          </thead>
          <tbody>
            {orders.map(order => (
              <tr key={order.id}>
                <td><strong>{order.number}</strong><br/><small>{t('SHOP_ORDER_LINES', {count: order.items.length})}</small></td>
                <td>{order.partner || 'Replayer'}</td>
                <td>{order.customer_name || '-'}<br/><small>{order.customer_email}</small></td>
                <td>{order.created_at ? new Date(order.created_at).toLocaleString(i18n.language) : '-'}</td>
                <td>{order.delivery_method === 'pickup' ? t('SHOP_PICKUP') : t('SHIPPING')}</td>
                <td>EUR {euros(order.subtotal_cents)}</td>
                <td>EUR {euros(order.shipping_cents)}</td>
                <td>EUR {euros(order.total_cents)}</td>
                <td>{statusLabel(order.status)}</td>
              </tr>
            ))}
            {orders.length === 0 && (
              <tr><td colSpan={9}>{t('SHOP_NO_ORDERS')}</td></tr>
            )}
          </tbody>
        </Table></div>
      </Card.Body>
    </Card>
  );
};

export default ShopReport;
